import Vue from 'vue';
import crossfilter from 'crossfilter2';
import { mutations, state as stateTypes } from './types';

export default {
  /**
   * Set cohorts mutation.
   * @param {*} state
   * @param {*} cohorts
   */
  [mutations.SET_COHORTS](state, cohorts) {
    state[stateTypes.COHORTS] = cohorts;
  },
  /**
   * Set cohort mutation.
   * @param {*} state
   * @param {*} cohort
   */
  [mutations.SET_COHORT](state, cohort) {
    state[stateTypes.COHORT] = cohort;
  },
  /**
   * Add cohort to cohorts.
   * @param {*} state
   * @param {*} cohort
   */
  [mutations.ADD_COHORT](state, cohort) {
    state[stateTypes.COHORTS] = [...state[stateTypes.COHORTS], cohort];
  },
  /**
   * Remove cohort from cohorts.
   * @param {*} state
   * @param {*} cohortID
   */
  [mutations.REMOVE_COHORT](state, cohortID) {
    state[stateTypes.COHORTS] = state[stateTypes.COHORTS].filter(
      cohort => cohort.id !== cohortID
    );
  },
  /**
   * Rename cohort from cohorts.
   * @param {*} state
   * @param {*} cohortId
   * @param {*} cohortName
   */
  [mutations.RENAME_COHORT](state, { cohortId, cohortName }) {
    state[stateTypes.COHORTS].forEach(cohort => {
      if (cohort.id == cohortId) {
        cohort.label = cohortName;
      }
    });
  },
  /**
   * Set loading mutation.
   * @param {Object} state
   * @param {Boolean} isLoading Flag whether or not we are waiting on data.
   */
  [mutations.SET_LOADING](state, isLoading) {
    state[stateTypes.IS_LOADING] = isLoading;
  },
  /**
   * Set collection mutation.
   * @param {Object} state Global cohort manager state.
   * @param {Object} collection
   */
  [mutations.SET_COLLECTION](state, collection) {
    state[stateTypes.COLLECTION] = collection;
  },
  /**
   * Add input variable to our array of variables.
   * @param {Object} state Global cohort manager state.
   * @param {Object} variable Input variable
   */
  [mutations.ADD_INPUT_VARIABLE](state, variable) {
    state[stateTypes.INPUT_VARIABLES].push(variable);
  },
  /**
   * Remove input variable from array.
   * @param {Object} state Global cohort manager state
   * @param {Object} variable Input variable to remove
   */
  [mutations.REMOVE_INPUT_VARIABLE](state, variable) {
    const inputVariables = state[stateTypes.INPUT_VARIABLES];
    const indexToRemove = inputVariables.findIndex(
      inputVar => inputVar.id === variable.id
    );
    Vue.delete(inputVariables, indexToRemove);
  },
  /**
   * Add output variable to our array of variables.
   * @param {Object} state Global cohort manager state.
   * @param {Object} variable Output variable
   */
  [mutations.ADD_OUTPUT_VARIABLE](state, variable) {
    state[stateTypes.OUTPUT_VARIABLES].push(variable);
  },
  /**
   * Remove output variable from array.
   * @param {Object} state Global cohort manager state
   * @param {Object} variable Output variable to remove
   */
  [mutations.REMOVE_OUTPUT_VARIABLE](state, variable) {
    const outputVariables = state[stateTypes.OUTPUT_VARIABLES];
    const indexToRemove = outputVariables.findIndex(
      inputVar => inputVar.id === variable.id
    );
    Vue.delete(outputVariables, indexToRemove);
  },
  /**
   * Initialize crossfilter.
   *
   * Crossfilter assists with managing filters across dimensions
   * and makes sure we don't send dimension filters to chart that is
   * using that dimension.
   *
   * @param {Object} state Global cohort manager state.
   * @param {*} data Patient records. Each index is a patient
   * with all appropriate metadata. (Demographics/Outcomes)
   */
  [mutations.INITIALIZE_CROSS_FILTER](state, data) {
    const xf = crossfilter(data);
    state[stateTypes.CROSS_FILTER] = xf;
    // Unfiltered data should only be initialized once
    state[stateTypes.UNFILTERED_DATA] = xf.all();
  },
  /**
   * Filters were added, update our filtered data.
   *
   * Crossfilter manages its own state and Vue cant
   * know about this to be reactive. What vue can track is
   * arrays, so whenever we update crossfilter, we want to
   * set our reactive array to our new crossfilter filtered
   * array. When this reactive array detects changes, it will
   * then propogate those changes to components using it.
   *
   * @param {Object} state Global cohort manager state.
   */
  [mutations.UPDATE_FILTERED_DATA](state) {
    const xf = state[stateTypes.CROSS_FILTER];
    state[stateTypes.FILTERED_DATA] = xf.allFiltered();
  },
  /**
   * Set input variables.
   * @param {*} state
   * @param {*} newInputVariables
   */
  [mutations.SET_INPUT_VARIABLES](state, newInputVariables) {
    state[stateTypes.INPUT_VARIABLES] = newInputVariables;
  },
  /**
   * Set output variables.
   * @param {*} state
   * @param {*} newOutputVariables
   */
  [mutations.SET_OUTPUT_VARIABLES](state, newOutputVariables) {
    state[stateTypes.OUTPUT_VARIABLES] = newOutputVariables;
  },
  /**
   * Set data.
   * @param {*} state
   * @param {*} newData
   */
  [mutations.SET_DATA](state, newData) {
    state[stateTypes.DATA] = newData;
  },
  /**
   * Add a dimension to crossfilter.
   *
   * We use dimensions to add filters.
   * @param {Object} state Global cohort manager state.
   * @param {String} dimensionName The name of our dimension. ("Sex")
   */
  [mutations.ADD_DIMENSION](state, { dimensionName, accessor, isArray }) {
    const xf = state[stateTypes.CROSS_FILTER];
    const dimensions = state[stateTypes.DIMENSIONS];
    // Dimensions are stateful and we want to make sure we are not initializing
    // dims that have been already initialized.
    // https://github.com/crossfilter/crossfilter/wiki/API-Reference#Dimension
    if (!(dimensionName in dimensions)) {
      const dimension = xf.dimension(accessor, isArray);
      Vue.set(dimensions, dimensionName, dimension);
    }
  },
  /**
   * Add filter to dimension
   * @param {Object} state Global cohort manager state.
   * @param {Object} filter An object with dimension and filter keys.
   */
  [mutations.ADD_FILTER](state, { dimension, filter }) {
    const dimensions = state[stateTypes.DIMENSIONS];
    const dim = dimensions[dimension];
    dim.filterFunction(filter);
  },
  /**
   * Remove dimension.
   * @param {Object} state
   * @param {String} dimensionName
   */
  [mutations.REMOVE_DIMENSION](state, dimensionName) {
    const dimensions = state[stateTypes.DIMENSIONS];
    const dimension = dimensions[dimensionName];
    // https://github.com/crossfilter/crossfilter/wiki/API-Reference#dimension_dispose
    dimension.dispose();
  },
  /**
   * Clear a dimension's filter.
   * @param {Object} state
   * @param {Object} dimension
   */
  [mutations.CLEAR_FILTER](state, { dimension }) {
    const dimensions = state[stateTypes.DIMENSIONS];
    const dim = dimensions[dimension];
    if (
      typeof dim !== 'undefined' &&
      typeof dim.currentFilter() !== 'undefined'
    ) {
      dim.filterAll();
    }
  },
  /**
   * Set pvals
   * @param {Object} state
   * @param {Array} pvals Array of objects with a pval and its corresponding outcome measure.
   */
  [mutations.SET_PVALS](state, pvals) {
    state[stateTypes.PVALS] = pvals;
  },
  /**
   * Set pvals request status
   * @param {Object} state
   * @param {status} either NULL, 'loading', or 'loaded'
   */
  [mutations.SET_PVALS_REQUEST_STATUS](state, status) {
    state[stateTypes.PVALS_REQUEST_STATUS] = status;
  },
  /**
   * Set pval threshold
   * @param {Object} state
   * @param {Array} threshold P value threshold.
   */
  [mutations.SET_PVAL_THRESHOLD](state, threshold) {
    state[stateTypes.PVAL_THRESHOLD] = threshold;
  },
  /**
   * Set ANOVA pvals
   * @param {Object} state
   * @param {Array} pvals Array of objects with a pval and its corresponding outcome measure.
   */
  [mutations.SET_ANOVA_PVALS](state, pvals) {
    state[stateTypes.ANOVA_PVALS] = pvals;
  },
  /**
   * Set ANOVA pvals request status
   * @param {Object} state
   * @param {status} either NULL, 'loading', or 'loaded'
   */
  [mutations.SET_ANOVA_PVALS_REQUEST_STATUS](state, status) {
    state[stateTypes.ANOVA_PVALS_REQUEST_STATUS] = status;
  },
  /**
   * Set Odds Ratio
   * @param {Object} state
   * @param {Array} results Array of objects with an odds ratio, confidence interval, pval and its corresponding outcome measure.
   */
  [mutations.SET_ODDS_RATIO](state, results) {
    state[stateTypes.ODDS_RATIO] = results;
  },
  /**
   * Set Odds Ratio error
   * @param {Object} state
   * @param {Array} error String describing the problem.
   */
  [mutations.SET_ODDS_RATIO_ERROR](state, error) {
    state[stateTypes.ODDS_RATIO_ERROR] = error;
  },
  /**
   * Set odds ratio request status
   * @param {Object} state
   * @param {status} either NULL, 'loading', or 'loaded'
   */
  [mutations.SET_ODDS_RATIO_REQUEST_STATUS](state, status) {
    state[stateTypes.ODDS_RATIO_REQUEST_STATUS] = status;
  },
  /**
   * Set highlighted subset
   * @param {Object} state
   * @param {Array} New highlighted subset.
   */
  [mutations.SET_HIGHLIGHTED_SUBSET](state, subset) {
    state[stateTypes.HIGHLIGHTED_SUBSET] = subset;
  },
  /**
   * Set comparison measure
   * @param {Object} state
   * @param {String} comparison measure
   */
  [mutations.SET_COMPARISON_MEASURE](state, measure) {
    state[stateTypes.COMPARISON_MEASURE] = measure;
  },
  /**
   * Set use long scale names.
   * @param {Object} state
   * @param {String} whether to use long scale names
   */
  [mutations.SET_USE_LONG_SCALE_NAMES](state, useLong) {
    state[stateTypes.USE_LONG_SCALE_NAMES] = useLong;
  },
  /**
   * Set query for particular dimension.
   * @param {Object} state
   * @param {Object} query Object with the keys, param, operator, value that represents a filter
   */
  [mutations.SET_QUERY](state, { dimension, query }) {
    const queries = state[stateTypes.QUERIES];

    if (!(dimension in queries)) {
      Vue.set(queries, dimension, []);
    }
    // Bar charts can have multiple queries (more than one study selected)
    // while histograms will just have a single range query.
    const dimensionQueries = Array.isArray(query) ? query : [query];
    queries[dimension] = dimensionQueries;
  },
  /**
   * Clear query for particular dimension.
   * @param {Object} state
   * @param {String} dimension
   */
  [mutations.CLEAR_QUERY](state, dimension) {
    const queries = state[stateTypes.QUERIES];

    if (dimension in queries) {
      Vue.delete(queries, dimension);
    }
  },
  /**
   * Reset cohort data back to orignal state.
   * @param {Object} state
   */
  [mutations.RESET_COHORTS](state) {
    state[stateTypes.COHORTS] = [];
    state[stateTypes.COHORT] = { id: null };
  },
  /**
   * Reset is loading back to original state.
   * @param {Object} state
   */
  [mutations.RESET_IS_LOADING](state) {
    state[stateTypes.IS_LOADING] = false;
  },
  /**
   * Reset collection back to original state.
   * @param {Object} state
   */
  [mutations.RESET_COLLECTION](state) {
    state[stateTypes.COLLECTION] = {};
  },
  /**
   * Reset unfiltered data back to original state.
   * @param {Object} state
   */
  [mutations.RESET_UNFILTERED_DATA](state) {
    state[stateTypes.UNFILTERED_DATA] = [];
  },
  /**
   * Reset filtered data back to original state.
   * @param {Object} state
   */
  [mutations.RESET_FILTERED_DATA](state) {
    state[stateTypes.FILTERED_DATA] = [];
  },
  /**
   * Reset input variables back to original state;
   * @param {Object} state
   */
  [mutations.RESET_INPUT_VARIABLES](state) {
    state[stateTypes.INPUT_VARIABLES] = [];
  },
  /**
   * Reset output variables back to original state.
   * @param {Object} state
   */
  [mutations.RESET_OUTPUT_VARIABLES](state) {
    state[stateTypes.OUTPUT_VARIABLES] = [];
  },
  /**
   * Reset crossfilter back to original state.
   * @param {Object} state
   */
  [mutations.RESET_CROSS_FILTER](state) {
    state[stateTypes.CROSS_FILTER] = null;
  },
  /**
   * Reset dimensions back to original state.
   * @param {Object} state
   */
  [mutations.RESET_DIMENSIONS](state) {
    state[stateTypes.DIMENSIONS] = {};
  },
  /**
   * Reset pvals back to original state.
   * @param {Object} state
   */
  [mutations.RESET_PVALS](state) {
    state[stateTypes.PVALS] = [];
  },
  /**
   * Reset ANOVA pvals back to original state.
   * @param {Object} state
   */
  [mutations.RESET_ANOVA_PVALS](state) {
    state[stateTypes.ANOVA_PVALS] = [];
  },
  /**
   * Reset odds ratio back to original state.
   * @param {Object} state
   */
  [mutations.RESET_ODDS_RATIO](state) {
    state[stateTypes.ODDS_RATIO] = {};
  },
  /**
   * Reset odds ratio error back to original state.
   * @param {Object} state
   */
  [mutations.RESET_ODDS_RATIO_ERROR](state) {
    state[stateTypes.ODDS_RATIO_ERROR] = null;
  },
  /**
   * Reset pval threshold back to original state.
   * @param {Object} state
   */
  [mutations.RESET_PVAL_THRESHOLD](state) {
    state[stateTypes.PVAL_THRESHOLD] = 'None';
  },
  /**
   * Reset highlighted subset back to original state.
   * @param {Object} state
   */
  [mutations.RESET_HIGHLIGHTED_SUBSET](state) {
    state[stateTypes.HIGHLIGHTED_SUBSET] = 'None';
  },
  /**
   * Reset queries back to original state.
   * @param {Object} state
   */
  [mutations.RESET_QUERIES](state) {
    state[stateTypes.QUERIES] = {};
  },
  /**
   * Increment request number and return the result via callback.
   * @param {Object} state
   */
  [mutations.INCREMENT_PVALS_REQUEST_NUM](state, { callback }) {
    state[stateTypes.PVALS_REQUEST_NUM] =
      state[stateTypes.PVALS_REQUEST_NUM] + 1;
    callback(state[stateTypes.PVALS_REQUEST_NUM]);
  },
  /**
   * Increment request number and return the result via callback.
   * @param {Object} state
   */
  [mutations.INCREMENT_ANOVA_PVALS_REQUEST_NUM](state, { callback }) {
    state[stateTypes.ANOVA_PVALS_REQUEST_NUM] =
      state[stateTypes.ANOVA_PVALS_REQUEST_NUM] + 1;
    callback(state[stateTypes.ANOVA_PVALS_REQUEST_NUM]);
  },
  /**
   * Increment request number and return the result via callback.
   * @param {Object} state
   */
  [mutations.INCREMENT_ODDS_RATIO_REQUEST_NUM](state, { callback }) {
    state[stateTypes.ODDS_RATIO_REQUEST_NUM] =
      state[stateTypes.ODDS_RATIO_REQUEST_NUM] + 1;
    callback(state[stateTypes.ODDS_RATIO_REQUEST_NUM]);
  },
  /**
   * Set help mode.
   * @param {Object} state
   * @param {Boolean} whether help mode is enabled.
   */
  [mutations.SET_HELP_MODE](state, helpMode) {
    state[stateTypes.HELP_MODE] = helpMode;
  },
  /**
   * Set sort mode.
   * @param {Object} state
   * @param {Boolean} whether help mode is enabled.
   */
  [mutations.SET_SORT](state, sort) {
    state[stateTypes.SORT] = sort;
  },
  /**
   * Set outcome based analysis.
   * @param {*} state
   * @param {*} cohorts
   */
  [mutations.SET_ANALYSIS_MODE](state, mode) {
    state[stateTypes.SELECTED_ANALYSIS_MODE] = mode;
  },
  /**
   * Set CV metadata.
   * @param {*} state
   * @param {*} cv_md
   */
  [mutations.SET_CV_METADATA](state, cv_md) {
    state[stateTypes.CV_METADATA] = cv_md;
  },
  /**
   * Set current tab in bottom-right analytics panel.
   * @param {*} state
   * @param {*} cv_md
   */
  [mutations.SET_CURRENT_ANALYTICS_TAB](state, tab) {
    state[stateTypes.CURRENT_ANALYTICS_TAB] = tab;
  },
};
